import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Course } from 'src/app/models/course.model';
import { FeedbackDto, UpsertFeedbackDto } from 'src/app/models/feedback.model';
import { user } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { CourseService } from 'src/app/services/course.service';
import { DateTimeService } from 'src/app/services/date-time.service';
import { FeedbackService } from 'src/app/services/feedback.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { FeedbackQuestionGroupComponent } from '../feedback-question-group/feedback-question-group.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    CheckboxModule,
    FloatLabelModule,
    ButtonModule,
    DropdownModule,
    RadioButtonModule,
    DatePipe,
    CalendarModule,
    FeedbackQuestionGroupComponent,
  ],
  templateUrl: './feedback-form.component.html',
  styleUrl: '../feedback.component.scss',
})
export class FeedbackFormComponent implements OnInit {
  @Input() existingFeedbackId?: number | null = null;
  @Input() mode: 'edit' | 'show' | 'create' = 'create';
  QUESTION_PREFIX = 'question_';
  feedback: FeedbackDto;
  courses: Course[];
  allowedUsers: user[];
  selectedCourse: Course | null = null;
  isSubmitting = false;
  form: FormGroup;

  constructor(
    private feedbackService: FeedbackService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private courseService: CourseService,
    private authService: AuthService,
    private datetimeService: DateTimeService,
    private userService: UserService,
    private router: Router,
  ) {}

  get infoCategory() {
    return this.feedback?.categories?.find(
      (cat) => cat.name === 'Allgemeine Informationen',
    );
  }

  get otherCategories() {
    return (
      this.feedback?.categories?.filter(
        (cat) => cat.name !== 'Allgemeine Informationen',
      ) || []
    );
  }

  ngOnInit() {
    if (this.existingFeedbackId) {
      this.loadExistingFeedback();
    } else {
      this.loadQuestions();
    }
    this.loadUsers();
    this.loadCourses();
  }

  get canWriteFeedback() {
    return this.authService.getUserPermissions().includes('Feedback.Write');
  }

  loadExistingFeedback() {
    this.feedbackService.getFeedbackById(this.existingFeedbackId).subscribe({
      next: (data) => {
        this.feedback = data;
        this.buildForm();
        this.patchFormWithFeedback(data);
      },
      error: () => {
        this.notificationService.error('Feedback konnte nicht geladen werden.');
      },
    });
  }

  loadUsers(): void {
    this.userService.getUsersObservable().subscribe((users) => {
      this.allowedUsers = users;
    });
  }

  patchFormWithFeedback(feedback: FeedbackDto) {
    if (!feedback.categories) return;

    if (feedback.courseId) {
      this.form.patchValue({ courseId: feedback.courseId });
    }
    if (feedback.date) {
      this.form.patchValue({
        date: new Date(feedback.date),
      });
    }
    if (feedback.creatorId) {
      this.form.patchValue({
        creatorId: feedback.creatorId,
      });
    }

    feedback.categories.forEach((cat) => {
      cat.questions.forEach((q) => {
        if (q.answer !== undefined && q.answer !== null) {
          this.form.get(this.QUESTION_PREFIX + q.id)?.setValue(q.answer);
        }
      });
    });
  }

  loadQuestions() {
    return this.feedbackService.getQuestions().subscribe({
      next: (data) => {
        this.feedback = data;
        this.buildForm();
      },
      error: () =>
        this.notificationService.error('Fehler beim Laden des Fragebogens'),
    });
  }

  loadCourses() {
    return this.courseService.getCourses().subscribe({
      next: (data) => (this.courses = data),
      error: (err) => console.log('Error: ' + err),
    });
  }

  onCourseChange(courseId: number) {
    this.selectedCourse = this.courses?.find((c) => c.courseId == courseId);
  }

  buildForm() {
    this.form = this.fb.group({
      courseId: ['', Validators.required],
      date: [this.datetimeService.getCurrentDate(), Validators.required],
      creatorId: [this.authService.getCurrentUserId(), Validators.required],
    });

    this.feedback.categories.forEach((cat) => {
      cat.questions.forEach((q) => {
        this.form.addControl(this.QUESTION_PREFIX + q.id, this.fb.control(''));
      });
    });

    this.form.get('courseId')?.valueChanges.subscribe((courseId) => {
      this.onCourseChange(courseId);
    });
  }

  submitForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.warn('Bitte füllen Sie die Pflichtfelder aus.');
      return;
    }

    const formValue = this.form.value;
    const answers = Object.keys(formValue)
      .filter((key) => key.startsWith(this.QUESTION_PREFIX))
      .map((key) => ({
        questionId: Number(key.replace(this.QUESTION_PREFIX, '')),
        value: formValue[key]?.toString(),
      }));

    const dto: UpsertFeedbackDto = {
      id: this.existingFeedbackId,
      creatorId: this.authService.getCurrentUserId(),
      courseId: formValue.courseId,
      date: this.datetimeService.formatDateToString(formValue.date),
      answers: answers,
    };

    this.isSubmitting = true;
    this.feedbackService.saveFeedback(dto).subscribe({
      next: (data) => {
        this.isSubmitting = false;
        this.form.reset();
        this.notificationService.success(
          this.existingFeedbackId
            ? 'Feedback erfolgreich aktualisiert.'
            : 'Feedback erfolgreich erstellt.',
        );
        this.router.navigate(['feedback']);
      },
      error: () => {
        this.isSubmitting = false;
        this.notificationService.error(
          'Feedback konnte nicht erfolgreich gespeichert werden.',
        );
      },
    });
  }

  isInvalid(ctrl: string): boolean {
    const c = this.form.get(ctrl);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }
}
